import { atom, useAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { CarrierCode } from "~/components/CarrierSelector";
import {
  Address,
  DeliveryType,
  StoreCart,
  StoreCartItem,
} from "~/shared-types";
import { stringToCurrency } from "~/utils/stringConversion";
import { components } from "~/shipmondo-schema-v3";
import { getOfferDetails } from "~/utils/getOfferDetails";

const getCanOnlyBePickedUp = (cart?: StoreCart) =>
  cart?.items.some((item) => item.product.attributes.canOnlyBePickedUp);

type AvailableDeliveryType = {
  type: DeliveryType;
  price: number | null;
  label: string;
};

export const storeCartAtom = atom<{
  cart?: StoreCart;
  carrierCode?: CarrierCode;
  receiverAddress?: Address;
  receiverName?: string;
  orderComment?: string;
  currentDeliveryType?: DeliveryType;
  currentDeliveryLocation?: {
    quote?: components["schemas"]["CreateQuoteResponse"];
    address?: Address;
    servicePointId?: string;
  };
}>({ carrierCode: "pdk" });

export const useStoreCart = (initialCart?: StoreCart) => {
  const [savedValues, setSavedValues] = useAtom(storeCartAtom);
  const canOnlyBePickedUp = getCanOnlyBePickedUp(initialCart);
  const store = useMemo(
    () => savedValues.cart?.store.attributes,
    [JSON.stringify(savedValues.cart?.store.attributes)]
  );
  const cart = savedValues.cart ?? initialCart;

  // Effect: Set initial cart
  useEffect(() => {
    if (!initialCart) return;

    setSavedValues((prev) => ({ ...prev, cart: initialCart }));
  }, [initialCart]);

  const priceDetails = useMemo(
    () =>
      getPriceDetails(cart, savedValues.currentDeliveryType, canOnlyBePickedUp),
    [cart, savedValues.currentDeliveryType, canOnlyBePickedUp]
  );

  const availableDeliveryTypes: AvailableDeliveryType[] = useMemo(() => {
    const pickupOption: AvailableDeliveryType = {
      type: "pickup",
      price: null,
      label: `Afhentes (${store?.shownAddress?.line1}, ${store?.shownAddress?.postCode} ${store?.shownAddress?.city})`,
    };
    const freeShippingOption: AvailableDeliveryType = {
      type: "freeShipping",
      price: 0,
      label: "Leveres (gratis)",
    };
    const availableDeliveryTypes: AvailableDeliveryType[] = [];

    if (canOnlyBePickedUp) {
      if (!store?.acceptsPickUp) return [];

      return [pickupOption];
    }

    if (store?.acceptsPickUp) {
      availableDeliveryTypes.push(pickupOption);
    }

    if (store?.deliveryType !== "none") {
      if (priceDetails.hasFreeDelivery) {
        availableDeliveryTypes.push(freeShippingOption);
      } else if (store?.deliveryType === "shipmondo") {
        availableDeliveryTypes.push({
          type: "shipmondo",
          price: null,
          label: "Leveres",
        });
      } else {
        availableDeliveryTypes.push({
          type: "shipping",
          price: priceDetails.summedDeliveryPrice,
          label: `Leveres (${stringToCurrency({
            amount: priceDetails.summedDeliveryPrice,
          })})`,
        });
      }
    }

    return availableDeliveryTypes;
  }, [
    canOnlyBePickedUp,
    store?.shownAddress,
    store?.deliveryType,
    priceDetails.hasFreeDelivery,
    priceDetails.summedDeliveryPrice,
    store?.deliveryType,
  ]);

  // Effect: Set initial delivery type
  useEffect(() => {
    if (!availableDeliveryTypes.length || savedValues.currentDeliveryType)
      return;

    setSavedValues((prev) => ({
      ...prev,
      currentDeliveryType: availableDeliveryTypes[0].type,
    }));
  }, [JSON.stringify(availableDeliveryTypes)]);

  // Effect: When the available delivery types change, make sure the shipping option stays selected
  useEffect(() => {
    const shippingTypes = [
      "shipping",
      "freeShipping",
      "shipmondo",
    ] as DeliveryType[];

    if (
      savedValues.currentDeliveryType &&
      shippingTypes.includes(savedValues.currentDeliveryType)
    ) {
      const correspondingDeliveryType = availableDeliveryTypes.find((type) =>
        shippingTypes.includes(type.type)
      );

      if (!correspondingDeliveryType) return;

      setSavedValues((prev) => ({
        ...prev,
        currentDeliveryType: correspondingDeliveryType.type,
      }));
    }
  }, [JSON.stringify(availableDeliveryTypes)]);

  const deliveryPrice = useMemo(() => {
    if (savedValues.currentDeliveryType === "shipmondo") {
      if (savedValues.currentDeliveryLocation?.quote) {
        return savedValues.currentDeliveryLocation.quote.price;
      }

      return null;
    }

    if (savedValues.currentDeliveryType === "freeShipping") {
      return 0;
    }

    return priceDetails.summedDeliveryPrice;
  }, [
    savedValues.currentDeliveryType,
    savedValues.currentDeliveryLocation,
    savedValues.currentDeliveryLocation?.quote?.price,
    priceDetails.summedDeliveryPrice,
    JSON.stringify(availableDeliveryTypes),
  ]);

  const total = useMemo(() => {
    if (!priceDetails.subTotal) return null;

    if (
      !savedValues.currentDeliveryType ||
      savedValues.currentDeliveryType === "pickup"
    )
      return priceDetails.subTotal;

    return priceDetails.subTotal + (deliveryPrice || 0);
  }, [priceDetails.subTotal, deliveryPrice, savedValues.currentDeliveryType]);

  return {
    setSavedValues,
    ...savedValues,
    ...priceDetails,
    total,
    deliveryPrice,
    canOnlyBePickedUp,
    availableDeliveryTypes,
  };
};

export const getSummedProductPrice = (items: StoreCartItem[]) => {
  return items.reduce((fullPrice: number, item: StoreCartItem) => {
    const { product, quantity } = item;
    const { discount } = getOfferDetails(product);

    const price = product.attributes.price - (discount || 0);

    return fullPrice + price * quantity;
  }, 0);
};

export const getSummedDeliveryPrice = (
  items: StoreCartItem[],
  storeDeliveryPrice: number
) => {
  const productsWithSpecialDeliveryPrice: StoreCartItem[] = [];
  const productsCoveredByStoreDeliveryPrice: StoreCartItem[] = [];

  items.forEach((item) => {
    if (
      item.product.attributes.deliveryPrice &&
      item.product.attributes.deliveryPrice > 0
    ) {
      productsWithSpecialDeliveryPrice.push(item);
    } else {
      productsCoveredByStoreDeliveryPrice.push(item);
    }
  });

  // No products with special delivery price
  if (productsWithSpecialDeliveryPrice.length === 0) return storeDeliveryPrice;

  const summedSpecialDeliveryPrice = productsWithSpecialDeliveryPrice.reduce(
    (fullPrice: number, item: StoreCartItem) =>
      fullPrice + item.product.attributes.deliveryPrice * item.quantity,
    0
  );

  // Only products with special delivery price
  if (productsCoveredByStoreDeliveryPrice.length === 0)
    return summedSpecialDeliveryPrice;

  // A mix of products with special delivery price and products covered by store delivery price
  return summedSpecialDeliveryPrice + storeDeliveryPrice;
};

export const getPriceDetails = (
  cart?: StoreCart,
  deliveryType?: DeliveryType,
  canOnlyBePickedUp?: boolean
) => {
  if (!cart) {
    return {
      subTotal: null,
      summedDeliveryPrice: null,
      hasFreeDelivery: false,
    };
  }

  const items = cart.items;
  const deliveryPrice = cart.store.attributes.deliveryPrice;
  const freeDeliveryPurchasePrice =
    cart.store.attributes.freeDeliveryPurchasePrice;
  const offersFreeDelivery = cart.store.attributes.offersFreeDelivery;

  const subTotal = getSummedProductPrice(items);

  if (canOnlyBePickedUp)
    return {
      subTotal,
      summedDeliveryPrice: 0,
      hasFreeDelivery: false,
    };

  const hasFreeDelivery =
    offersFreeDelivery &&
    freeDeliveryPurchasePrice &&
    subTotal >= freeDeliveryPurchasePrice;

  const summedDeliveryPrice = hasFreeDelivery
    ? 0
    : getSummedDeliveryPrice(items, deliveryPrice);

  return {
    subTotal,
    summedDeliveryPrice,
    hasFreeDelivery,
  };
};
